
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'

import authModule from '@/store/auth'
import dashboardModule from '@/store/dashboard'
import whitelabelModule from '@/store/whitelabel'
import companyModule from '@/store/company'
import venueModule from '@/store/venue'
import VueGoogleAutocomplete from 'vue-google-autocomplete'

import { Company, Venue, isValidCompany } from '@/types/Company'
import { Whitelabel } from '@/types/Whitelabel'
import { TextFieldRules } from '@/utils/enums'
import {
  compareNested,
  structuredGoogleLocation
} from '@/utils/utility-manager'

@Component({
  name: 'VenueEditor',
  components: {
    VueGoogleAutocomplete
  }
})
class VenueEditor extends Vue {
  @Prop() readonly edit?: boolean

  public rules = TextFieldRules
  public isValidCompany = true
  public disableForm = false

  public form: Partial<Venue> = {
    name: '',
    url: '',
    phone: '',
    email: '',
    emailPublic: false,
    facebook: '',
    instagram: '',
    whatsapp: '',
    enable: false,
    address: {
      street: '',
      streetNo: '',
      zipCode: undefined,
      city: '',
      countryCode: '',
      location: undefined
    },
    company: '',
    externalId: ''
  }

  public get auth() {
    return authModule
  }

  public get companyError() {
    if (this.isValidCompany) {
      return []
    } else {
      return 'Diesem Unternehmen fehlen Pflichtangaben!'
    }
  }

  @Watch('form.company')
  async test() {
    const data = await companyModule.getCompany(this.form.company as string)
    if (!isValidCompany(data)) {
      this.isValidCompany = false
    } else {
      this.isValidCompany = true
    }
  }

  public get whitelabel(): Whitelabel {
    return whitelabelModule.whitelabel
  }

  public get company(): Company | undefined {
    return companyModule.company
  }

  public get companies(): Partial<Company>[] {
    return companyModule.companies
  }

  public get venue(): Venue | undefined {
    return venueModule.venue
  }

  public get whitelabels() {
    return whitelabelModule.whitelabels
  }

  public get relatedCompanies() {
    return companyModule.companies.filter(
      company => company.whitelabel === this.whitelabel._id
    )
  }

  public async mounted(): Promise<void> {
    if (authModule.isPartner || authModule.isLightPartner) {
      if (!companyModule.company) return console.warn('no company found')
      const data = await companyModule.getCompany(companyModule.company!._id)
      console.log(
        '🚀 ~ file: VenueEditor.vue ~ line 241 ~ VenueEditor ~ mounted ~ data',
        data
      )
      if (!isValidCompany(data)) {
        dashboardModule.newError(
          'Dem aktuellen Unternehmen fehlen Pflichtangaben!'
        )
        this.disableForm = true
      }
    }
  }

  public async created(): Promise<void> {
    if (authModule.isSuperadmin || authModule.isAdmin) {
      await companyModule.fetchAll({ limit: 0, lean: true })
    }
    if (!this.edit) return
    if (!this.venue) {
      await venueModule.fetch(this.$route.params.vid)
    }
    this.form = JSON.parse(JSON.stringify(this.venue))
  }

  public onPlaceChanged(_: any, placeResultData: any) {
    this.form.address = structuredGoogleLocation(placeResultData)
  }

  public async saveVenue(): Promise<void> {
    if (!this.edit) {
      venueModule.create({
        venue: this.form,
        company:
          authModule.isPartner || authModule.isLightPartner
            ? companyModule.company!._id
            : (this.form.company! as string),
        whitelabel: this.whitelabel._id
      })
    } else {
      const payload: Partial<Venue> = await compareNested(this.form, this.venue)
      Object.assign(payload, { _id: this.venue!._id })
      console.log(JSON.stringify(this.companies))
      const venueCompany = this.companies.find(el => {
        if (typeof this.venue!.company === 'string') {
          return el._id === this.venue!.company
        } else {
          return el._id === this.venue!.company._id
        }
      })
      if (venueCompany) {
        venueModule.update({
          whitelabel: venueCompany.whitelabel ?? this.whitelabel._id,
          venue: payload
        })
      } else {
        dashboardModule.newError('Geschäft konnte nicht aktualisiert werden')
      }
    }
    document.dispatchEvent(new Event('close-venue'))
  }

  public toggleAutocompleteLabel(): void {
    const label = this.$refs.autocompleteLabel as HTMLElement
    if (label.classList.contains('v-label--active')) {
      label.classList.remove('v-label--active', 'primary--text')
    } else {
      label.classList.add('v-label--active', 'primary--text')
    }
  }
}

export default VenueEditor
